import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  formatDate,
} from "@odingard/design-system";
import type { ExtractionResult, StudioDocument } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { documentsApi } from "../../lib/api";
import {
  DocumentUpload,
  extractionSummary,
  extractionTone,
  formatBytes,
} from "../../components/DocumentsPicker";

// Shared Studio Documents (ODS-DOCUMENTS-001 P4).
//
// The one place a customer puts a file into Studio. Storage and extraction both
// belong to the shared Documents Platform, so a document imported here is the
// same artifact Policy AI evidence, Contract AI and Proposal AI reference — there
// is no per-product copy of the bytes and no per-product uploader.
export function Documents() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [extractions, setExtractions] = useState<Record<string, ExtractionResult | string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const documents = useAsync(
    async () => (orgId ? (await documentsApi.list(orgId, { status: "active" })).documents : []),
    [orgId],
  );

  const extract = async (document: StudioDocument) => {
    setBusy(document.id);
    try {
      const result = await documentsApi.extract(orgId, document.id);
      setExtractions((prev) => ({ ...prev, [document.id]: result }));
    } catch (err) {
      setExtractions((prev) => ({
        ...prev,
        [document.id]: err instanceof Error ? err.message : "Extraction failed.",
      }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Documents</h1>
          <p>
            Files Studio holds for this organization. Documents stores the bytes and reads the text;
            every product references the same record rather than keeping its own copy.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader title="Import a document" />
        <CardBody>
          <DocumentUpload orgId={orgId} onUploaded={() => documents.reload()} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Stored documents" />
        <CardBody>
          {documents.loading ? <LoadingState label="Loading documents…" /> : null}
          {documents.error ? (
            <ErrorState description={documents.error.message} onRetry={documents.reload} />
          ) : null}
          {!documents.loading && !documents.error && (documents.data ?? []).length === 0 ? (
            <EmptyState
              title="No documents yet"
              description="Import a policy, contract or attachment above. It becomes available to every Studio product that consumes governed documents."
            />
          ) : null}
          {(documents.data ?? []).length > 0 ? (
            <DataTable
              caption="Documents stored by the shared Documents Platform"
              columns={[
                {
                  key: "name",
                  header: "Document",
                  render: (row: StudioDocument) => (
                    <div>
                      <strong>{row.displayName || row.filename}</strong>
                      <div className="muted-note">{row.mimeType}</div>
                    </div>
                  ),
                },
                { key: "size", header: "Size", render: (row: StudioDocument) => formatBytes(row.size) },
                {
                  key: "checksum",
                  header: "Checksum",
                  render: (row: StudioDocument) => <code>{row.checksum.slice(0, 12)}…</code>,
                },
                {
                  key: "version",
                  header: "Version",
                  render: (row: StudioDocument) => `v${row.latestVersion}`,
                },
                {
                  key: "created",
                  header: "Imported",
                  render: (row: StudioDocument) => formatDate(row.createdAt),
                },
                {
                  key: "extraction",
                  header: "Text extraction",
                  render: (row: StudioDocument) => {
                    const state = extractions[row.id];
                    if (typeof state === "string") {
                      return <span className="muted-note">{state}</span>;
                    }
                    if (state) {
                      return (
                        <div>
                          <Badge tone={extractionTone(state)}>
                            {state.status === "extracted"
                              ? "Extracted"
                              : (state.failureCode ?? "Unsupported")}
                          </Badge>
                          <div className="muted-note">{extractionSummary(state)}</div>
                          {state.extractionChecksum ? (
                            <div className="muted-note">
                              <code>{state.extractionChecksum.slice(0, 12)}…</code>
                            </div>
                          ) : null}
                        </div>
                      );
                    }
                    return (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busy === row.id}
                        onClick={() => extract(row)}
                      >
                        {busy === row.id ? "Reading…" : "Read text"}
                      </Button>
                    );
                  },
                },
              ]}
              rows={documents.data ?? []}
            />
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}
